import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { SUBJECTS, SUBJECT_META } from '../../data/standards'
import { useKUs } from '../../hooks/useKUs'
import { useAuth } from '../../providers/AuthProvider'
import { useActiveExperiment } from '../../providers/ActiveExperimentProvider'
import { useStandards, TreeNode, StandardNode, gradeLabel } from '../../providers/StandardsProvider'
import { KnowledgeUnit, Subject } from '../../types'
import StandardDetailPanel from '../standard-detail/StandardDetailPanel'
import KUDetailPanel from '../ku-detail/KUDetailPanel'

const LAST_STD_KEY = 'kosmos_last_std'

// ── Filter a tree to only nodes reachable from expIds ────────────────────────
function filterTreeByExperiment(nodes: TreeNode[], expIds: Set<string>): TreeNode[] {
  const result: TreeNode[] = []
  for (const tn of nodes) {
    if (tn.node.normalized_type === 'Standard') {
      if (tn.node.code && expIds.has(tn.node.code)) result.push(tn)
    } else {
      const filtered = filterTreeByExperiment(tn.children, expIds)
      if (filtered.length > 0) result.push({ ...tn, children: filtered })
    }
  }
  return result
}

// ── Grouping summary panel (shown when a domain/cluster is selected) ─────────
interface GroupingPanelProps {
  node: StandardNode
  stdsWithData: Set<string>
  meta: { color: string; bg: string }
  onSelectStd: (code: string) => void
  onNavigateToRoot: () => void
  getDescendantLeafs: (nodeId: string) => StandardNode[]
}

function GroupingPanel({
  node,
  stdsWithData,
  meta,
  onSelectStd,
  onNavigateToRoot,
  getDescendantLeafs,
}: GroupingPanelProps) {
  const leafs = getDescendantLeafs(node.id)
  const withData = leafs.filter((l) => l.code && stdsWithData.has(l.code)).length

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Breadcrumb */}
      <Box
        sx={{
          px: 2,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography
          onClick={onNavigateToRoot}
          variant="caption"
          sx={{ fontSize: 12, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
        >
          Standards
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 12 }}>
          ›
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 700, color: meta.color }}>
          {node.label}
        </Typography>
      </Box>

      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: meta.color,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          {node.statement_type}
        </Typography>
        <Typography
          sx={{ fontSize: 20, fontWeight: 900, color: meta.color, letterSpacing: '-0.01em', lineHeight: 1.2, mb: 0.75 }}
        >
          {node.label}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
          {leafs.length} standards · {withData} with KUs extracted
        </Typography>
      </Box>

      {/* Grade introduction + critical areas (grade nodes only) */}
      {node.statement_type === 'Grade' &&
        (node.introduction || (node.critical_areas && node.critical_areas.length > 0)) && (
          <Box
            sx={{
              px: 2.5,
              py: 2,
              bgcolor: '#f0f7ff',
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            {node.introduction && (
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: 'text.primary',
                  lineHeight: 1.65,
                  mb: node.critical_areas?.length ? 1.5 : 0,
                }}
              >
                {node.introduction}
              </Typography>
            )}
            {node.critical_areas && node.critical_areas.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: meta.color,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    mb: 0.75,
                  }}
                >
                  Critical Areas
                </Typography>
                {node.critical_areas.map((area, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: meta.color, flexShrink: 0, mt: '1px' }}>
                      {i + 1}.
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.6 }}>{area}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

      {/* Cluster heading (cluster nodes only) */}
      {node.statement_type === 'Cluster' && node.body && (
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: '#fafbfc',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.6, fontStyle: 'italic' }}>
            {node.body}
          </Typography>
        </Box>
      )}

      {/* Standards list */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, pb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {leafs.map((std) => {
            const hasData = std.code ? stdsWithData.has(std.code) : false
            return (
              <Box
                key={std.id}
                onClick={() => std.code && onSelectStd(std.code)}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: hasData ? meta.color + '60' : 'divider',
                  bgcolor: hasData ? meta.bg : 'background.paper',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  '&:hover': { borderColor: meta.color, bgcolor: meta.bg },
                  transition: 'all 0.12s',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: meta.color, display: 'block', fontSize: 11, mb: 0.25 }}
                  >
                    {std.code}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: 11, lineHeight: 1.5, display: 'block' }}
                  >
                    {std.body}
                  </Typography>
                </Box>
                {hasData && (
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#16a34a', flexShrink: 0, mt: 0.5 }} />
                )}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

// ── Recursive tree node renderer ──────────────────────────────────────────────
interface TreeNodeRowProps {
  treeNode: TreeNode
  depth: number
  selectedNodeId: string | null
  stdsWithData: Set<string>
  meta: { color: string; bg: string }
  onSelectNode: (node: StandardNode) => void
  expandedKeys: Set<string>
  onToggle: (key: string) => void
}

function TreeNodeRow({
  treeNode,
  depth,
  selectedNodeId,
  stdsWithData,
  meta,
  onSelectNode,
  expandedKeys,
  onToggle,
}: TreeNodeRowProps) {
  const { node, children } = treeNode
  const isGrouping = node.normalized_type === 'Standard Grouping'
  const isLeaf = node.normalized_type === 'Standard' && node.statement_type !== 'Component'
  const isExpanded = expandedKeys.has(node.id)
  const active = selectedNodeId === node.id

  if (isGrouping) {
    return (
      <Box>
        <ListItemButton
          onClick={() => {
            onToggle(node.id)
            onSelectNode(node)
          }}
          sx={{
            pl: 1.5 + depth * 1.25,
            pr: 1.5,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderLeft: `3px solid ${active ? meta.color : 'transparent'}`,
            bgcolor: active ? meta.bg : 'transparent',
            '&:hover': { bgcolor: active ? meta.bg : 'action.hover' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: depth === 0 ? 12 : 11,
                color: active ? meta.color : depth === 0 ? 'primary.main' : 'text.secondary',
              }}
            >
              {node.label}
            </Typography>
            {node.statement_type === 'Cluster' && node.body && (
              <Typography
                sx={{
                  fontSize: 10,
                  color: 'text.disabled',
                  lineHeight: 1.35,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {node.body}
              </Typography>
            )}
          </Box>
          {isExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: active ? meta.color : 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: active ? meta.color : 'text.secondary' }} />
          )}
        </ListItemButton>
        <Collapse in={isExpanded}>
          {children.map((child) => (
            <TreeNodeRow
              key={child.node.id}
              treeNode={child}
              depth={depth + 1}
              selectedNodeId={selectedNodeId}
              stdsWithData={stdsWithData}
              meta={meta}
              onSelectNode={onSelectNode}
              expandedKeys={expandedKeys}
              onToggle={onToggle}
            />
          ))}
        </Collapse>
        {depth === 0 && <Divider />}
      </Box>
    )
  }

  if (isLeaf) {
    return (
      <Box>
        <ListItemButton
          onClick={() => onSelectNode(node)}
          sx={{
            pl: 1.5 + depth * 1.25,
            pr: 1.5,
            py: 0.875,
            borderLeft: `3px solid ${active ? meta.color : 'transparent'}`,
            bgcolor: active ? meta.bg : 'transparent',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: active ? meta.bg : 'action.hover' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: meta.color, display: 'block', fontSize: 10.5, mb: 0.15 }}
            >
              {node.code}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: active ? 'text.primary' : 'text.secondary',
                fontSize: 11,
                lineHeight: 1.4,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {node.body}
            </Typography>
          </Box>
          {node.code && stdsWithData.has(node.code) && (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a', flexShrink: 0, ml: 1 }} />
          )}
        </ListItemButton>
        {children.length > 0 && isExpanded && (
          <Box sx={{ pl: 2 }}>
            {children.map((child) => (
              <TreeNodeRow
                key={child.node.id}
                treeNode={child}
                depth={depth + 1}
                selectedNodeId={selectedNodeId}
                stdsWithData={stdsWithData}
                meta={meta}
                onSelectNode={onSelectNode}
                expandedKeys={expandedKeys}
                onToggle={onToggle}
              />
            ))}
          </Box>
        )}
      </Box>
    )
  }

  return null
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function StandardsPage() {
  const { profile } = useAuth()
  const { activeExperiment, activeExperimentLoading } = useActiveExperiment()
  const { fetchStandardsWithKUs } = useKUs()
  const { loading: stdLoading, getGrades, getDomainTree, findNode, getDescendantLeafs } = useStandards()

  const [subject, setSubject] = useState<Subject>('Math')
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedKuId, setSelectedKuId] = useState<string | null>(null)
  const [stdsWithData, setStdsWithData] = useState<Set<string>>(new Set())
  const [showPin, setShowPin] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [expFilter, setExpFilter] = useState<'all' | 'experiment'>('experiment')
  const [pinStdId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_STD_KEY)
    } catch {
      return null
    }
  })

  const allGrades = getGrades(subject)
  const defaultGrade = (() => {
    if (!profile?.primary_grade) return allGrades[0] ?? 'K'
    const pg = String(profile.primary_grade)
    return allGrades.includes(pg) ? pg : allGrades[0] ?? 'K'
  })()
  const [activeGrade, setActiveGrade] = useState<string>(defaultGrade)

  // ── Derive experiment metadata ─────────────────────────────────────────────
  const expData = useMemo(() => {
    if (!activeExperiment) return null
    const standards = activeExperiment.standards
    const grades = activeExperiment.grades
    if (!Array.isArray(standards) || !Array.isArray(grades)) return null
    return {
      ids: new Set(standards),
      subject: activeExperiment.subject,
      grades,
    }
  }, [activeExperiment])

  // When an experiment becomes active, auto-switch to experiment filter
  useEffect(() => {
    if (activeExperiment) {
      setExpFilter('experiment')
    }
  }, [activeExperiment?.id]) // eslint-disable-line

  // When experiment filter is on, ensure subject + grade are valid
  useEffect(() => {
    if (expFilter === 'experiment' && expData) {
      if (subject !== expData.subject) {
        setSubject(expData.subject)
        setSelectedNodeId(null)
        setExpandedKeys(new Set())
      }
      if (!expData.grades.includes(activeGrade) && expData.grades.length > 0) {
        setActiveGrade(expData.grades[0])
        setSelectedNodeId(null)
        setExpandedKeys(new Set())
      }
    }
  }, [expFilter, expData?.subject, expData?.grades.join(',')]) // eslint-disable-line

  useEffect(() => {
    if (allGrades.length > 0 && !allGrades.includes(activeGrade)) {
      const pg = profile?.primary_grade ? String(profile.primary_grade) : null
      setActiveGrade(pg && allGrades.includes(pg) ? pg : allGrades[0])
    }
  }, [subject, allGrades]) // eslint-disable-line

  const handleSubjectChange = (s: Subject) => {
    setSubject(s)
    setSelectedNodeId(null)
    setSelectedKuId(null)
    setExpandedKeys(new Set())
  }

  useEffect(() => {
    fetchStandardsWithKUs().then(setStdsWithData)
  }, [fetchStandardsWithKUs])
  useEffect(() => {
    if (pinStdId && !selectedNodeId) setShowPin(true)
  }, [pinStdId, selectedNodeId])

  // Derive the selected node object
  const selectedNode = selectedNodeId ? findNode(selectedNodeId) : null
  const isGroupingSelected = selectedNode?.normalized_type === 'Standard Grouping'
  const selectedStdCode = !isGroupingSelected && selectedNode?.code ? selectedNode.code : null

  const handleSelectNode = (node: StandardNode) => {
    setSelectedNodeId(node.id)
    setSelectedKuId(null)
    setShowPin(false)
    if (node.normalized_type === 'Standard' && node.code) {
      try {
        localStorage.setItem(LAST_STD_KEY, node.code)
      } catch {}
    }
  }

  const handleSelectStdCode = (code: string) => {
    const tree = getDomainTree(subject, activeGrade)
    const findByCode = (nodes: TreeNode[]): StandardNode | null => {
      for (const t of nodes) {
        if (t.node.code === code) return t.node
        const found = findByCode(t.children)
        if (found) return found
      }
      return null
    }
    const node = findByCode(tree)
    if (node) handleSelectNode(node)
  }

  const handleToggle = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const handleNavigateToRoot = () => {
    setSelectedNodeId(null)
    setSelectedKuId(null)
  }
  const handleSelectKU = (ku: KnowledgeUnit) => setSelectedKuId(ku.id)

  // ── Derive display data based on experiment filter ─────────────────────────
  const showingExperiment = expFilter === 'experiment' && !!expData && !!activeExperiment
  const visibleSubjects: Subject[] = showingExperiment ? [expData!.subject] : [...SUBJECTS]
  const visibleGrades = showingExperiment ? expData!.grades : allGrades

  const meta = SUBJECT_META[subject]
  const domainTree = getDomainTree(subject, activeGrade)
  const displayTree = showingExperiment ? filterTreeByExperiment(domainTree, expData!.ids) : domainTree

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* ── Left panel ────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: 320,
          flexShrink: 0,
          bgcolor: '#f8fafc',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* View toggle: Experiment / All Standards */}
        <Box
          sx={{
            display: 'flex',
            flexShrink: 0,
            borderBottom: '2px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {activeExperimentLoading ? (
            /* Still loading — show placeholder so we don't flash "All Standards" */
            <Box sx={{ flex: 1, py: 1.5, textAlign: 'center' }}>
              <CircularProgress size={14} />
            </Box>
          ) : activeExperiment && expData ? (
            <>
              <Box
                onClick={() => setExpFilter('experiment')}
                sx={{
                  flex: 1,
                  py: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  color: expFilter === 'experiment' ? '#1e3a5f' : '#94a3b8',
                  borderBottom: expFilter === 'experiment' ? '2px solid #1e3a5f' : '2px solid transparent',
                  mb: '-2px',
                  transition: 'all 0.1s',
                  userSelect: 'none',
                  '&:hover': { color: '#1e3a5f' },
                }}
              >
                {activeExperiment.name}
                <Typography
                  sx={{ fontSize: 9.5, color: expFilter === 'experiment' ? '#64748b' : '#cbd5e1', fontWeight: 400 }}
                >
                  {expData.ids.size} standards
                </Typography>
              </Box>
              <Box
                onClick={() => setExpFilter('all')}
                sx={{
                  flex: 1,
                  py: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  color: expFilter === 'all' ? '#1e3a5f' : '#94a3b8',
                  borderBottom: expFilter === 'all' ? '2px solid #1e3a5f' : '2px solid transparent',
                  mb: '-2px',
                  transition: 'all 0.1s',
                  userSelect: 'none',
                  '&:hover': { color: '#1e3a5f' },
                }}
              >
                All Standards
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, py: 1, textAlign: 'center', fontWeight: 700, fontSize: 12, color: '#1e3a5f' }}>
              All Standards
            </Box>
          )}
        </Box>

        {/* Subject tabs */}
        <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Tabs
            value={subject}
            onChange={(_, v) => handleSubjectChange(v as Subject)}
            variant="fullWidth"
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
          >
            {visibleSubjects.map((s) => (
              <Tab
                key={s}
                value={s}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={{ fontSize: 13 }}>{SUBJECT_META[s].icon}</span>
                    <span>{s}</span>
                  </Box>
                }
                sx={{ fontWeight: 700, fontSize: 11 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Grade selector */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 1.5,
            py: 0.75,
            flexShrink: 0,
          }}
        >
          {stdLoading ? (
            <CircularProgress size={16} />
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={visibleGrades.includes(activeGrade) ? activeGrade : visibleGrades[0] ?? ''}
                onChange={(e) => {
                  setActiveGrade(e.target.value)
                  setExpandedKeys(new Set())
                  setSelectedNodeId(null)
                }}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: meta.color,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: meta.color + '60' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: meta.color },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: meta.color },
                }}
              >
                {visibleGrades.map((g) => {
                  const isPrimary = g === String(profile?.primary_grade)
                  return (
                    <MenuItem key={g} value={g} sx={{ fontSize: 12 }}>
                      {gradeLabel(g)}
                      {isPrimary ? ' ★' : ''}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Pin banner */}
        {showPin && pinStdId && (
          <Box
            sx={{
              bgcolor: '#fef3c7',
              borderBottom: '1px solid #fde68a',
              px: 1.5,
              py: 0.875,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 14 }}>📍</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706', fontSize: 10, display: 'block' }}>
                Resume
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#d97706',
                  fontSize: 10,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {pinStdId}
              </Typography>
            </Box>
            <Box
              onClick={() => handleSelectStdCode(pinStdId)}
              sx={{
                bgcolor: '#d97706',
                color: '#fff',
                borderRadius: 1,
                px: 1.25,
                py: 0.3,
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Go →
            </Box>
            <Typography
              onClick={() => setShowPin(false)}
              sx={{ color: '#d97706', fontSize: 14, cursor: 'pointer', lineHeight: 1 }}
            >
              ✕
            </Typography>
          </Box>
        )}

        {/* TOC tree */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {stdLoading || activeExperimentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : displayTree.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pt: 8,
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: 24 }}>📭</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                No {subject} standards for Grade {activeGrade}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {displayTree.map((treeNode) => (
                <TreeNodeRow
                  key={treeNode.node.id}
                  treeNode={treeNode}
                  depth={0}
                  selectedNodeId={selectedNodeId}
                  stdsWithData={stdsWithData}
                  meta={meta}
                  onSelectNode={handleSelectNode}
                  expandedKeys={expandedKeys}
                  onToggle={handleToggle}
                />
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* ── Right panel ───────────────────────────────────────────────── */}
      <Box
        sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}
      >
        {!selectedNode ? (
          /* Empty state */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'primary.main',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#fff', lineHeight: 1, userSelect: 'none' }}>
                K
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: 22, color: 'primary.main', letterSpacing: '0.06em' }}>
              ARDENT KOSMOS
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 13 }}>
              Select a standard or domain to begin
            </Typography>
          </Box>
        ) : selectedKuId && selectedStdCode ? (
          <KUDetailPanel
            key={selectedKuId}
            standardId={selectedStdCode}
            kuId={selectedKuId}
            onBack={() => setSelectedKuId(null)}
            onSelectKU={(id) => setSelectedKuId(id)}
            onNavigateToRoot={handleNavigateToRoot}
          />
        ) : isGroupingSelected ? (
          <GroupingPanel
            key={selectedNodeId}
            node={selectedNode}
            stdsWithData={stdsWithData}
            meta={meta}
            onSelectStd={handleSelectStdCode}
            onNavigateToRoot={handleNavigateToRoot}
            getDescendantLeafs={getDescendantLeafs}
          />
        ) : selectedStdCode ? (
          <StandardDetailPanel
            key={selectedStdCode}
            standardId={selectedStdCode}
            onSelectKU={handleSelectKU}
            onNavigateToRoot={handleNavigateToRoot}
          />
        ) : null}
      </Box>
    </Box>
  )
}
